import { useState, useRef, useCallback } from 'react'
import { Stack, Typography, Box, Popper } from '@mui/material'

interface ScrollPickerProps {
  items: string[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  itemHeight?: number;
}

const ScrollPicker = ({ items, value, onChange, label, itemHeight = 36 }: ScrollPickerProps) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const listCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const index = items.indexOf(value)
    if (index >= 0) {
      node.scrollTo({ top: index * itemHeight - itemHeight * 2 })
    }
  }, [value, items])

  const handleSelect = (item: string) => {
    onChange(item)
    setOpen(false)
  }

  return (
    <Stack sx={{ flex: 1 }} gap={0.5}>
      <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', textAlign: 'center' }}>
        {label}
      </Typography>
      <Box
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        tabIndex={0}
        role="combobox"
        aria-expanded={open}
        aria-label={label}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen((prev) => !prev)
        }}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 40,
          border: `1px solid ${open ? theme.palette.primary.main : theme.palette.grey[200]}`,
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: 16,
          fontWeight: 600,
          color: theme.palette.grey[600],
          userSelect: 'none',
          transition: 'border-color 0.2s ease',
          '&:hover': { borderColor: theme.palette.primary.main },
        })}
      >
        {value}
      </Box>
      {open && (
        <>
          <Box
            onClick={() => setOpen(false)}
            sx={{ position: 'fixed', inset: 0, zIndex: 1399 }}
          />
          <Popper
            open
            anchorEl={anchorRef.current}
            placement="bottom"
            style={{ zIndex: 1400 }}
            modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
          >
            <Box
              ref={listCallbackRef}
              sx={(theme) => ({
                width: anchorRef.current?.offsetWidth || 'auto',
                maxHeight: itemHeight * 5,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '12px',
                p: 0.5,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[4],
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#D7DAEA', borderRadius: 4 },
                scrollbarWidth: 'thin',
                scrollbarColor: '#D7DAEA transparent',
              })}
            >
              {items.map((item) => {
                const isSelected = item === value
                return (
                  <Box
                    key={item}
                    onClick={() => handleSelect(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleSelect(item)
                    }}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: itemHeight,
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? 'primary.main' : 'text.secondary',
                      backgroundColor: isSelected ? 'action.selected' : 'transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: isSelected ? 'action.selected' : 'grey.100',
                      },
                    }}
                  >
                    {item}
                  </Box>
                )
              })}
            </Box>
          </Popper>
        </>
      )}
    </Stack>
  )
}

export default ScrollPicker